import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Modal, Space, Button, Input, Select, Checkbox, Typography } from 'antd';
import { SoundOutlined, SendOutlined } from '@ant-design/icons';
import { getTemplateById, getTemplatesByLanguage } from '../../utils/announcementTemplates';

const { Text } = Typography;

const AnnouncementModal = React.memo(({ 
    visible, 
    onCancel, 
    onSend, 
    sending,
    availableVoices = []
}) => {
    // Internal state - this component manages its own state to prevent parent re-renders
    const [announcementMessage, setAnnouncementMessage] = useState('');
    const [announcementLanguage, setAnnouncementLanguage] = useState('en');
    const [useTemplate, setUseTemplate] = useState(false);
    const [announcementReadAloud, setAnnouncementReadAloud] = useState(false);
    const [selectedVoice, setSelectedVoice] = useState(() => {
        // Load saved voice preference from localStorage
        return localStorage.getItem('announcement_voice_preference') || null;
    });

    // Memoize templates by language
    const templatesByLanguage = useMemo(() => {
        return getTemplatesByLanguage(announcementLanguage);
    }, [announcementLanguage]);

    // Reset state when modal closes
    useEffect(() => {
        if (!visible) {
            setAnnouncementMessage('');
            setAnnouncementLanguage('en');
            setUseTemplate(false);
            setAnnouncementReadAloud(false);
            // Don't reset selectedVoice - keep user preference
        }
    }, [visible]);

    // Handle message change
    const handleMessageChange = useCallback((e) => {
        setAnnouncementMessage(e.target.value);
    }, []);

    // Handle template selection
    const handleTemplateChange = useCallback((templateId) => {
        const template = getTemplateById(templateId);
        if (template && template.message) {
            setAnnouncementMessage(template.message);
        } else {
            setAnnouncementMessage('');
        }
    }, []);

    // Handle language change
    const handleLanguageChange = useCallback((lang) => {
        setAnnouncementLanguage(lang);
        setAnnouncementMessage('');
        if (lang === 'ur') {
            setAnnouncementReadAloud(false);
        }
    }, []);

    // Handle use template checkbox
    const handleUseTemplateChange = useCallback((e) => {
        setUseTemplate(e.target.checked);
        if (!e.target.checked) {
            setAnnouncementMessage('');
            setAnnouncementLanguage('en');
        }
    }, []);

    // Handle voice selection
    const handleVoiceChange = useCallback((voiceName) => {
        setSelectedVoice(voiceName);
        localStorage.setItem('announcement_voice_preference', voiceName);
    }, []);

    // Handle send
    const handleSend = useCallback(() => {
        if (!announcementMessage.trim()) return;
        onSend(announcementMessage.trim(), announcementReadAloud);
    }, [announcementMessage, announcementReadAloud, onSend]);

    // Handle cancel
    const handleCancel = useCallback(() => {
        onCancel();
    }, [onCancel]);

    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <SoundOutlined style={{ color: '#faad14' }} />
                    <span>Broadcast Announcement</span>
                </div>
            }
            open={visible}
            onCancel={handleCancel}
            footer={[
                <Space key="footer" style={{ marginTop: '16px' }}>
                    <Button key="cancel" onClick={handleCancel}>
                        Cancel
                    </Button>
                    <Button
                        key="send"
                        type="primary"
                        icon={<SendOutlined />}
                        loading={sending}
                        onClick={handleSend}
                        disabled={!announcementMessage.trim()}
                    >
                        Broadcast
                    </Button>
                </Space>
            ]}
            width={700}
            centered
        >
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                {/* Choose Template or Custom */}
                <div>
                    <Checkbox
                        checked={useTemplate}
                        onChange={handleUseTemplateChange}
                    >
                        Choose from Template
                    </Checkbox>
                </div>

                {/* Template Selection - Only show if useTemplate is true */}
                {useTemplate && (
                    <>
                        {/* Language Selection */}
                        <div>
                            <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                                Select Language
                            </Text>
                            <Select
                                style={{ width: '100%' }}
                                value={announcementLanguage}
                                onChange={handleLanguageChange}
                            >
                                <Select.Option value="en">English</Select.Option>
                                <Select.Option value="ur">اردو (Urdu)</Select.Option>
                            </Select>
                        </div>

                        {/* Template Selection */}
                        <div>
                            <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                                Select a Message
                            </Text>
                            <Select
                                style={{ width: '100%' }}
                                placeholder="Choose an encouraging message"
                                onChange={handleTemplateChange}
                                allowClear
                                showSearch
                                filterOption={(input, option) => {
                                    const template = templatesByLanguage.find(t => t.id === option.value);
                                    if (!template) return false;
                                    const searchText = input.toLowerCase();
                                    return (
                                        template.title.toLowerCase().includes(searchText) ||
                                        template.message.toLowerCase().includes(searchText)
                                    );
                                }}
                                optionFilterProp="children"
                            >
                                {templatesByLanguage.map(template => (
                                    <Select.Option key={template.id} value={template.id}>
                                        {template.title}
                                    </Select.Option>
                                ))}
                            </Select>
                        </div>
                    </>
                )}

                {/* Message Text Area */}
                <div>
                    <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                        {useTemplate ? 'Message (Edit as needed)' : 'Custom Message'} (max 500 characters)
                    </Text>
                    <Input.TextArea
                        value={announcementMessage}
                        onChange={handleMessageChange}
                        placeholder={useTemplate ? "Select a template above to see the message, then you can edit it..." : "Enter your custom announcement message..."}
                        rows={4}
                        maxLength={500}
                        showCount={{
                            formatter: ({ count, maxLength }) => `${count} / ${maxLength}`,
                            show: true
                        }}
                        style={{
                            marginBottom: '0',
                            paddingBottom: '24px'
                        }}
                        autoFocus={!useTemplate}
                    />
                </div>

                {/* Text-to-Speech Option - Only show for English language */}
                {announcementLanguage === 'en' && (
                    <div>
                        <Checkbox
                            checked={announcementReadAloud}
                            onChange={(e) => setAnnouncementReadAloud(e.target.checked)}
                        >
                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <SoundOutlined style={{ color: '#1890ff' }} />
                                <Text>Read announcement aloud automatically for all users</Text>
                            </span>
                        </Checkbox>
                        <div style={{ marginTop: '4px', marginLeft: '24px', fontSize: '12px', color: '#8c8c8c' }}>
                            When enabled, the announcement will be automatically read using text-to-speech for all participants
                        </div>
                        
                        {/* Voice Selection - Only show when read aloud is enabled */}
                        {announcementReadAloud && availableVoices.length > 0 && (
                            <div style={{ marginTop: '12px', marginLeft: '24px' }}>
                                <Text strong style={{ display: 'block', marginBottom: '8px', fontSize: '13px' }}>
                                    Select Voice
                                </Text>
                                <Select
                                    style={{ width: '100%' }}
                                    value={selectedVoice}
                                    onChange={handleVoiceChange}
                                    placeholder="Select a voice"
                                    showSearch
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().includes(input.toLowerCase())
                                    }
                                >
                                    {availableVoices.map((voice) => (
                                        <Select.Option key={voice.name} value={voice.name}>
                                            {voice.name} {voice.lang ? `(${voice.lang})` : ''}
                                        </Select.Option>
                                    ))}
                                </Select>
                                <div style={{ marginTop: '4px', fontSize: '11px', color: '#8c8c8c' }}>
                                    Preview: Click to hear how this voice sounds
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </Space>
        </Modal>
    );
});

AnnouncementModal.displayName = 'AnnouncementModal';

export default AnnouncementModal;

